import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_STATE, toCount } from '../../utils/mlb-the-show/storage.js';
import { uid } from '../../utils/mlb-the-show/format.js';

const withGames = (games, extra) => {
  const ids = new Set(games.map((game) => game.id));
  return [...games, ...extra.filter((game) => !ids.has(game.id))];
};

export default function useTrackerData(storage) {
  const [data, setData] = useState(EMPTY_STATE);
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const storageRef = useRef(storage);
  const dataRef = useRef(data);
  const generation = useRef(0);
  const loadedRef = useRef(false);
  storageRef.current = storage;
  dataRef.current = data;

  useEffect(() => {
    if (!storage) return undefined;
    let cancelled = false;
    const ticket = generation.current + 1;
    generation.current = ticket;
    loadedRef.current = false;
    setReady(false);
    setLoaded(false);
    setError(null);

    storage
      .load()
      .then((next) => {
        if (cancelled || ticket !== generation.current) return;
        loadedRef.current = true;
        setData(next);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled || ticket !== generation.current) return;
        setError(err?.message || 'Could not load the tracker');
      })
      .finally(() => {
        if (!cancelled && ticket === generation.current) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [storage, attempt]);

  const commit = useCallback((updater) => {
    if (!loadedRef.current) return;
    const ticket = generation.current;
    setData((prev) => {
      if (ticket !== generation.current) return prev;
      const next = updater(prev);
      storageRef.current?.save(next).catch((err) => {
        if (ticket === generation.current) setError(err?.message || 'Could not save the tracker');
      });
      return next;
    });
  }, []);

  const addPlayer = useCallback((name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    commit((current) => ({
      ...current,
      players: [...current.players, { id: uid(), name: trimmed }],
    }));
  }, [commit]);

  /** Removes the player and their games; returns a function that puts both back. */
  const removePlayer = useCallback((id) => {
    const { players, games } = dataRef.current;
    const index = players.findIndex((player) => player.id === id);
    if (index < 0) return () => {};
    const player = players[index];
    const theirGames = games.filter((game) => game.home === id || game.away === id);
    commit((current) => ({
      players: current.players.filter((p) => p.id !== id),
      games: current.games.filter((game) => game.home !== id && game.away !== id),
    }));
    return () =>
      commit((current) => {
        if (current.players.some((p) => p.id === id)) return current;
        const nextPlayers = [...current.players];
        nextPlayers.splice(Math.min(index, nextPlayers.length), 0, player);
        return { players: nextPlayers, games: withGames(current.games, theirGames) };
      });
  }, [commit]);

  const addGame = useCallback((game) => {
    const next = {
      id: `g${uid()}`,
      createdAt: Date.now(),
      home: game.home,
      away: game.away,
      homeScore: toCount(game.homeScore),
      awayScore: toCount(game.awayScore),
      innings: toCount(game.innings, 9) || 9,
      date: game.date,
    };
    commit((current) => ({ ...current, games: [...current.games, next] }));
    return next;
  }, [commit]);

  /** Deletes the game; returns a function that puts it back. */
  const removeGame = useCallback((id) => {
    const game = dataRef.current.games.find((g) => g.id === id);
    if (!game) return () => {};
    commit((current) => ({ ...current, games: current.games.filter((g) => g.id !== id) }));
    return () =>
      commit((current) => {
        const known = new Set(current.players.map((p) => p.id));
        if (!known.has(game.home) || !known.has(game.away)) return current;
        return { ...current, games: withGames(current.games, [game]) };
      });
  }, [commit]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    ...data,
    ready,
    loaded,
    error,
    addPlayer,
    removePlayer,
    addGame,
    removeGame,
    retry,
  };
}
